<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { usersApi, type User } from '@/services/api/users';
import { useAuthStore } from '@/stores/auth';
import { useNotifier } from '@/composables/notifier';
import { usePasswordStrength } from '@/composables/usePasswordStrength';
import AccountSubNav from './AccountSubNav.vue';

const auth = useAuthStore();
const notify = useNotifier();
const user = ref<(User & { password?: string; confirmPassword?: string }) | null>(null);
const submitting = ref(false);

const passwordRef = computed(() => user.value?.password ?? '');
const passwordStrength = usePasswordStrength(passwordRef);

onMounted(async () => {
  const id = auth.currentUser?.id;
  if (id !== undefined) {
    user.value = (await usersApi.get(id)) as User & {
      password?: string;
      confirmPassword?: string;
    };
  }
});

async function submit(): Promise<void> {
  if (!user.value || submitting.value) return;
  submitting.value = true;
  try {
    await usersApi.update(user.value as User & { id: number });
    notify.info('Account updated.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div>
    <AccountSubNav />
    <form v-if="user" class="account-form" novalidate @submit.prevent="submit">
      <h2 class="account-form__title">Settings</h2>

      <label class="account-form__field" for="settings-email">
        <span>Email</span>
        <input id="settings-email" v-model="user.email" name="email" type="email" />
      </label>

      <label class="account-form__field" for="settings-name">
        <span>Name</span>
        <input id="settings-name" v-model="(user as any).name" name="name" type="text" />
      </label>

      <label class="account-form__field" for="settings-password">
        <span>Password</span>
        <input id="settings-password" v-model="user.password" name="password" type="password" />
      </label>

      <label class="account-form__field" for="settings-confirm">
        <span>Confirm Password</span>
        <input
          id="settings-confirm"
          v-model="user.confirmPassword"
          name="confirmPassword"
          type="password"
        />
      </label>

      <label class="account-form__field">
        <span>Password strength</span>
        <meter min="1" max="4" :value="passwordStrength" low="2" high="3" optimum="4" />
      </label>

      <button type="submit" :disabled="submitting" class="account-form__submit">
        {{ submitting ? 'Saving…' : 'Update account' }}
      </button>
    </form>
    <p v-else class="account-form__loading">Loading…</p>
  </div>
</template>

<style scoped lang="scss" src="./account-form.scss"></style>
